import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronLeft, ChevronRight, FileText, Car, History, X } from "lucide-react";
import { API } from "@/contexts/AuthContext";

// Searchable repository of all completed inspections
const FOHInspectionArchive = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const navigate = useNavigate();

  const fetchArchive = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20"
      });
      if (search) params.append("search", search);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const res = await axios.get(`${API}/foh/archive?${params}`);
      setInspections(res.data.inspections);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      console.error("Failed to fetch archive:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchive();
  }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchArchive();
  };

  const fetchVehicleHistory = async (reg) => {
    setSelectedVehicle(reg);
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/foh/vehicle-history/${reg}`);
      setVehicleHistory(res.data);
    } catch (err) {
      console.error("Failed to fetch vehicle history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Inspection Archive</h1>
            <p className="text-slate-500">{total} completed inspections</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/app/foh")}>
            <ChevronLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>

        {/* Search & Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-slate-500 mb-1">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Reg, customer, make, model..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1">From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="w-40">
                <Label className="text-xs text-slate-500 mb-1">To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <Button type="submit">Search</Button>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => {
                  setSearch("");
                  setDateFrom("");
                  setDateTo("");
                  setPage(1);
                  setTimeout(fetchArchive, 0);
                }}
              >
                Clear
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-slate-500">Loading archive...</p>
            </CardContent>
          </Card>
        ) : inspections.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No inspections found</p>
              <p className="text-sm mt-2">Try adjusting your search criteria</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3 mb-6">
              {inspections.map(insp => (
                <Card key={insp.inspection_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <Car className="w-5 h-5 text-slate-400" />
                          <span className="font-semibold text-slate-800">{insp.reg}</span>
                          <span className="text-slate-500">{insp.make} {insp.model}</span>
                          <span className="text-xs text-slate-400">
                            {formatDate(insp.completed_at)}
                          </span>
                        </div>
                        {insp.customer_name && (
                          <p className="text-sm text-slate-500 mt-1 ml-8">{insp.customer_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          {insp.red_count > 0 && (
                            <Badge className="bg-red-100 text-red-700">{insp.red_count} Red</Badge>
                          )}
                          {insp.amber_count > 0 && (
                            <Badge className="bg-amber-100 text-amber-700">{insp.amber_count} Amber</Badge>
                          )}
                          {insp.green_count > 0 && (
                            <Badge className="bg-green-100 text-green-700">{insp.green_count} Green</Badge>
                          )}
                        </div>
                        {insp.total_estimate > 0 && (
                          <span className="font-semibold text-slate-700">
                            £{insp.total_estimate.toFixed(2)}
                          </span>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fetchVehicleHistory(insp.reg)}
                        >
                          <History className="w-4 h-4 mr-1" /> History
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/app/foh/inspection/${insp.inspection_id}`)}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button 
                  variant="outline" 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* Vehicle History Modal */}
        {selectedVehicle && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <CardHeader className="border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Vehicle History: {selectedVehicle}
                    </CardTitle>
                    {vehicleHistory && (
                      <p className="text-sm text-slate-500 mt-1">
                        {vehicleHistory.inspection_count} inspection{vehicleHistory.inspection_count !== 1 ? 's' : ''} on record
                      </p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedVehicle(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto max-h-[60vh]">
                {loadingHistory ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : vehicleHistory?.history?.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    No inspection history found
                  </div>
                ) : (
                  <div className="divide-y">
                    {vehicleHistory?.history?.map((hist, idx) => (
                      <div key={hist.inspection_id} className="p-4 hover:bg-slate-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-medium">{formatDate(hist.completed_at)}</span>
                            {hist.customer_name && (
                              <span className="text-sm text-slate-500 ml-2">• {hist.customer_name}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {hist.item_count.red > 0 && (
                              <Badge className="bg-red-100 text-red-700 text-xs">{hist.item_count.red}</Badge>
                            )}
                            {hist.item_count.amber > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">{hist.item_count.amber}</Badge>
                            )}
                            {hist.total_estimate > 0 && (
                              <span className="text-sm font-medium">£{hist.total_estimate.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        {hist.items?.length > 0 && (
                          <div className="text-sm text-slate-600 pl-2 border-l-2 border-slate-200">
                            {hist.items.filter(i => i.status !== "green").slice(0, 5).map((item, i) => (
                              <div key={i} className="flex items-center gap-2 py-0.5">
                                <span className={`w-2 h-2 rounded-full ${
                                  item.status === "red" ? "bg-red-500" : "bg-amber-500"
                                }`}></span>
                                <span>{item.item_label || item.category}</span>
                              </div>
                            ))}
                            {hist.items.filter(i => i.status !== "green").length > 5 && (
                              <p className="text-xs text-slate-400 mt-1">
                                +{hist.items.filter(i => i.status !== "green").length - 5} more items
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default FOHInspectionArchive;
